import React, { type ReactNode } from 'react';
import {
  PROMO_BANNER_DENSITY_REGULAR,
  PROMO_BANNER_TONE_INFO,
  PROMO_BANNER_VARIANT_SUBTLE,
  type PromoBannerDensity,
  type PromoBannerTone,
  type PromoBannerVariant,
} from './PromoBanner.constants';
import {
  Actions,
  Body,
  Description,
  DismissButton,
  IconSlot,
  Root,
  Title,
  TitleRow,
} from './PromoBanner.styled';

export interface PromoBannerProps {
  /** Visible title — usually a string, but accepts richer nodes. */
  title: ReactNode;
  /**
   * Optional second line. Pass a string for the typical case or a
   * richer node when the message includes inline links.
   */
  description?: ReactNode;
  /**
   * Optional leading icon — typically a Material symbol or react-icons
   * glyph. The slot is sized at 28×28; the icon scales to fit.
   */
  icon?: ReactNode;
  /** Tone vocabulary — info / success / caution / error / promo. */
  tone?: PromoBannerTone;
  /** Visual treatment — subtle (default) or prominent (filled). */
  variant?: PromoBannerVariant;
  /** `compact` for an inline notice: smaller, wrapping title, actions at the row's end. */
  density?: PromoBannerDensity;
  /**
   * Slot for action buttons. Typically a `<Button variant="text">` row,
   * but accepts any node. Rendered below the description.
   */
  actions?: ReactNode;
  /**
   * When provided, a dismiss `×` button appears at the right edge of
   * the banner. Calling `onDismiss` is the consumer's cue to unmount /
   * persist the dismissal — the molecule itself does NOT manage its
   * own visibility.
   */
  onDismiss?: () => void;
  /** Override the dismiss button's aria-label. Defaults to "Dismiss". */
  dismissAriaLabel?: string;
  /**
   * Strongly recommended when `tone="error"` or `tone="caution"` —
   * stamps `role="alert"` so screen readers announce the banner
   * immediately. Other tones default to `role="status"` (polite).
   */
  ariaLive?: 'off' | 'polite' | 'assertive';
  className?: string;
  dataTestId?: string;
}

const DismissGlyph = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M6 6 18 18 M18 6 6 18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

/**
 * Heads-up banner for non-blocking announcements: new feature
 * promotions, upgrade nudges, success / error toasts that should stick
 * around longer than a transient toast, etc.
 *
 * Stays presentational — visibility is owned by the consumer (the
 * `onDismiss` callback is the cue to unmount or persist the dismissal
 * to localStorage / preferences).
 *
 * @example
 *   <PromoBanner
 *     tone="promo"
 *     icon={<MdAutoAwesome />}
 *     title="New: Templates"
 *     description="Start from a pre-built process to save time."
 *     actions={
 *       <>
 *         <Button variant="text" onClick={dismiss}>Maybe later</Button>
 *         <Button variant="filled" onClick={openTemplates}>Try templates</Button>
 *       </>
 *     }
 *     onDismiss={dismiss}
 *   />
 */
const PromoBanner: React.FC<PromoBannerProps> = ({
  title,
  description,
  icon,
  tone = PROMO_BANNER_TONE_INFO,
  variant = PROMO_BANNER_VARIANT_SUBTLE,
  density = PROMO_BANNER_DENSITY_REGULAR,
  actions,
  onDismiss,
  dismissAriaLabel = 'Dismiss',
  ariaLive,
  className,
  dataTestId = 'promo-banner',
}) => {
  // Derive the role + announcement loudness so they're always
  // semantically consistent. ARIA spec: `role="alert"` implies
  // assertive announcement; the two MUST agree.
  //
  //   - `ariaLive="off"` always wins → role degrades to `status` so an
  //     explicit "don't announce" can't be silently overridden by an
  //     error tone.
  //   - `ariaLive="assertive"` (or tone="error") → role="alert" +
  //     assertive.
  //   - `tone="caution"` → polite (urgent enough to interrupt prose,
  //     not urgent enough to be assertive).
  //   - Everything else → role="status" with no aria-live set.
  const alerting = ariaLive !== 'off' && (ariaLive === 'assertive' || tone === 'error');
  const role: 'alert' | 'status' = alerting ? 'alert' : 'status';
  const liveValue: 'off' | 'polite' | 'assertive' | undefined = (() => {
    if (ariaLive !== undefined) return ariaLive;
    if (role === 'alert') return 'assertive';
    if (tone === 'caution') return 'polite';
    return undefined;
  })();

  return (
    <Root
      $tone={tone}
      $variant={variant}
      $density={density}
      role={role}
      aria-live={liveValue}
      className={className}
      data-testid={dataTestId}
      data-tone={tone}
      data-variant={variant}
      data-density={density}
    >
      {icon && (
        <IconSlot
          $density={density}
          aria-hidden="true"
          data-testid={`${dataTestId}__icon`}
        >
          {icon}
        </IconSlot>
      )}
      <Body $density={density}>
        <TitleRow $density={density}>
          <Title $density={density} data-testid={`${dataTestId}__title`}>
            {title}
          </Title>
        </TitleRow>
        {description !== undefined && description !== null && (
          <Description data-testid={`${dataTestId}__description`}>
            {description}
          </Description>
        )}
        {actions && (
          <Actions $density={density} data-testid={`${dataTestId}__actions`}>
            {actions}
          </Actions>
        )}
      </Body>
      {onDismiss && (
        <DismissButton
          type="button"
          onClick={onDismiss}
          aria-label={dismissAriaLabel}
          data-testid={`${dataTestId}__dismiss`}
        >
          <DismissGlyph />
        </DismissButton>
      )}
    </Root>
  );
};

export default PromoBanner;
